import { mapIdToCalendarDate } from "@dailywars/core/engine";
import { isMedalEligibleDate, type MedalTier } from "@dailywars/core/medals";

import { FlameIcon, flameKindForStreak } from "#/components/FlameIcon";
import { MapPreviewImage } from "#/components/MapPreviewImage";
import { MedalIcon } from "#/components/MedalIcon";
import { PlayMapCta } from "#/components/PlayMapCta";
import { SectionHeader } from "#/components/SectionHeader";
import { Badge } from "#/components/ui/badge";
import { Card, CardContent, CardHeader } from "#/components/ui/card";
import type { AccessResult } from "#/lib/mapAccessUi";

import type { LeaderboardEntry } from "./-play.labels";
import { MapDayLeaderboard } from "./-play.mapDayLeaderboard";
import { HERO_PREVIEW_BOX_PX } from "./-play.previewSizing";

export function DailyMapColumn({
  title,
  mapId,
  bestDays,
  bestMedalTier,
  leaderboardEntries,
  access,
  dailyStreak,
}: {
  title: string;
  mapId: string;
  bestDays: number | null;
  bestMedalTier: MedalTier | null;
  leaderboardEntries: LeaderboardEntry[];
  access: AccessResult;
  dailyStreak: number | null;
}) {
  const medalsVisible = isMedalEligibleDate(mapIdToCalendarDate(mapId));

  return (
    <Card
      className="mx-auto flex h-full w-full min-w-0 flex-1 flex-col text-center"
      style={{ maxWidth: HERO_PREVIEW_BOX_PX }}
    >
      <CardHeader className="flex flex-col items-center gap-2 text-center">
        <SectionHeader>{title}</SectionHeader>
        {dailyStreak !== null ? <DailyStreakBadge dailyStreak={dailyStreak} /> : null}
      </CardHeader>

      <CardContent className="flex flex-1 flex-col items-center gap-6">
        <DailyMapHeroPreview mapId={mapId} />

        <PlayMapCta mapId={mapId} access={access} style={{ maxWidth: HERO_PREVIEW_BOX_PX }} />

        <div className="flex w-full flex-col gap-3">
          <YourBestScorePanel bestDays={bestDays} bestMedalTier={bestMedalTier} />

          <MapDayLeaderboard
            entries={leaderboardEntries}
            mapId={mapId}
            medalsVisible={medalsVisible}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function DailyStreakBadge({ dailyStreak }: { dailyStreak: number }) {
  const kind = flameKindForStreak(dailyStreak);

  return (
    <Badge className="inline-flex items-center gap-1.5 bg-background px-4 py-3 text-sm text-foreground">
      {kind ? <FlameIcon kind={kind} size={16} /> : null}
      Streak: {dailyStreak}
      {kind ? <FlameIcon kind={kind} size={16} /> : null}
    </Badge>
  );
}

function YourBestScorePanel({
  bestDays,
  bestMedalTier,
}: {
  bestDays: number | null;
  bestMedalTier: MedalTier | null;
}) {
  return (
    <div className="flex w-full flex-col gap-1 rounded-lg bg-background p-3 text-center ring-1 ring-foreground/10">
      <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Your Best Score
      </p>
      {bestDays === null ? (
        <p className="text-sm text-muted-foreground">Not played yet</p>
      ) : (
        <p className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold tabular-nums text-foreground">
          {bestMedalTier ? <MedalIcon tier={bestMedalTier} size={16} /> : null}
          {bestDays} {bestDays === 1 ? "day" : "days"}
        </p>
      )}
    </div>
  );
}

function DailyMapHeroPreview({ mapId }: { mapId: string }) {
  return (
    <MapPreviewImage
      mapId={mapId}
      maxSize={HERO_PREVIEW_BOX_PX}
      fillWidth
      className="rounded-xl border shadow-lg"
    />
  );
}
